/**
 * ArrayCell Component
 *
 * Displays array values using the ChipsList pattern with "+N" counter and tooltip.
 * Provides consistent UX for array fields across all tables by wrapping the canonical
 * ChipsList component with sensible defaults for table cell display.
 *
 * Features:
 * - Configurable maxVisible items before showing "+N"
 * - Hover tooltip showing all values
 * - Customizable chip variant and size
 * - Handles empty arrays gracefully
 */

import React from 'react';

import { ChipsList } from '@/components/ChipsList';

export interface ArrayCellProps {
  /** Array of values to display */
  values: unknown[];
  /** Maximum number of items to show before "+N" counter (default: 1) */
  maxVisible?: number;
  /** Chip style variant (default: 'default') */
  variant?: 'default' | 'brand' | 'info' | 'filter';
  /** Chip size (default: 'xs') */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Renders array values as chips with "+N" pattern
 *
 * Usage:
 * ```tsx
 * <ArrayCell values={['value1', 'value2', 'value3']} maxVisible={1} />
 * // Renders: [value1] +2
 * ```
 */
export const ArrayCell: React.FC<ArrayCellProps> = React.memo(
  ({ values, maxVisible = 1, variant = 'default', size = 'xs', className }) => {
    // Handle empty or invalid arrays
    if (!values || values.length === 0) {
      return <span className="text-chariot-text-secondary">-</span>;
    }

    // Convert all values to strings for ChipsList
    const stringValues = values.map(String);

    return (
      <ChipsList
        items={stringValues}
        maxVisible={maxVisible}
        variant={variant}
        size={size}
        wrapInTooltip
        className={className}
      />
    );
  }
);

ArrayCell.displayName = 'ArrayCell';
